import React from "react";
import { Button } from "@shared/ui";
import { RadioGroup } from "@headlessui/react";
import Account from "../components/Account";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";

import { updateIgnition } from "../lib/axle";

import { useEffect, useState } from "react";
import { ReturningUserEnum } from "../lib/constants";
import _, { get, size } from "lodash";
import { getCarrierLogo } from "../lib/carrier";
import { signOut } from "aws-amplify/auth";

interface WelcomeBackProps {
  step: string;
  nextStep: (nextStep: string, noHistory?: boolean) => void;
  posthog: any;
  setLoginInformation: (info: any) => void;
  client: any;
  session: any;
  setSession: (session: any) => void;
  ruxTokenData: RuxTokenData | null;
  setCognitoAuthTokens: (cognitoAuthTokens: any) => void;
  setRuxTokenData: (ruxTokenData: RuxTokenData | null) => void;
  setIsUserAuthenticated: (isUserAuthenticated: boolean) => void;
  setIntegration: (integration: string) => void;
}

const scopes = ["account:link", "account:read", "account:write"] as const;
type Scope = (typeof scopes)[number];

export interface TokenAccount {
  id: string;
  scopes: Scope[];
  createdAt: string; // Creation date in ISO string format
}

export interface RuxTokenData {
  accounts: Array<{
    id: string;
    carrier: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
  carriers: Array<{
    id: string;
    name: string;
    hasImage: boolean;
    image?: string;
    integrations?: any;
  }>;
  isValid: boolean;
}

const WelcomeBack = ({
  step,
  nextStep,
  posthog,
  setLoginInformation,
  client,
  session,
  setSession,
  setCognitoAuthTokens,
  setRuxTokenData,
  ruxTokenData,
  setIsUserAuthenticated,
  setIntegration,
}: WelcomeBackProps) => {
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<
    RuxTokenData["accounts"][number] | null
  >(get(ruxTokenData, "accounts[0]", null));

  useEffect(() => {
    if (!ruxTokenData) {
      nextStep("carriers", true);
    }
  }, [ruxTokenData, nextStep]);

  useEffect(() => {
    // Track page view
    posthog.capture("$pageview", { step });
  }, [posthog, step, nextStep]);

  const handleAccountSelect = (index: number) => {
    setSelectedAccountIndex(index);
  };

  const handleUseExistingAccount = async () => {
    if (!ruxTokenData) {
      return;
    }

    // Get the selected account based on the index
    const account = ruxTokenData.accounts[selectedAccountIndex];

    // Set login information with the carrier from the selected account
    if (account && account.carrier) {
      // The session's carrier carries the integration types (V123 vs legacy) that
      // getAccount branches on; the token's carrier is display-only.
      const carrier =
        session.carriers.find((c: any) => c.id === account.carrier) ??
        ruxTokenData.carriers.find((c: any) => c.id === account.carrier);
      const integration = get(account, "integration");

      if (!carrier) {
        console.error("Carrier not found for account:", account);
        return;
      }

      if (!integration) {
        console.error("Integration not found for account:", account);
        return;
      }

      // Update the ignition session with returningUser set to "re-link". Login's
      // re-link auto-login skips its own carrier save, so it happens here.
      try {
        const returningUser: ReturningUserEnum = "re-link";
        const updateResult = await updateIgnition(session.id, {
          returningUser,
          carrier: account.carrier,
          integration,
        });
        setSession({
          ...session,
          returningUser,
        });

        if (updateResult && updateResult[1]) {
          console.error("Error updating ignition session:", updateResult[1]);
        }
      } catch (error) {
        console.error("Exception updating ignition session:", error);
      }

      carrier.image = getCarrierLogo(carrier);

      setIntegration(integration);

      setLoginInformation({
        account: account.id,
        carrier,
        integration,
      });

      // Track user choice
      posthog.capture("welcome_back_use_existing_axle_passport", {
        carrier: account.carrier,
      });

      // Go to login page
      nextStep("login");
    }
  };

  const handleChooseDifferent = async () => {
    // Update the ignition session with returningUser set to "returning-user-new-link"
    try {
      const returningUser: ReturningUserEnum = "new-account";
      const updateResult = await updateIgnition(session.id, {
        returningUser,
      });

      setSession({
        ...session,
        returningUser,
      });

      setLoginInformation({
        account: null,
        carrier: null,
        integration: null,
      });

      if (updateResult && updateResult[1]) {
        console.error("Error updating ignition session:", updateResult[1]);
      }
    } catch (error) {
      console.error("Exception updating ignition session:", error);
    }

    // Track user choice
    posthog.capture("welcome_back_choose_different_axle_passport");

    // Go to carriers page
    nextStep("carriers");
  };

  // Handler for the "These are not my accounts" button
  const handleNotMyAccounts = async () => {
    // Track user choice with distinct event name
    posthog.capture("welcome_back_dont_recognize_axle_passport");

    // Update the ignition session with returningUser set to null
    try {
      const returningUser: ReturningUserEnum = "new-user";
      const updateResult = await updateIgnition(session.id, {
        returningUser,
      });
      setSession({
        ...session,
        returningUser,
      });

      if (updateResult && updateResult[1]) {
        console.error("Error updating ignition session:", updateResult[1]);
      }
    } catch (error) {
      console.error("Exception updating ignition session:", error);
    }

    setLoginInformation({
      account: null,
      carrier: null,
      integration: null,
    });

    console.log("Signing out user...");
    await signOut();

    setCognitoAuthTokens(null);
    setIsUserAuthenticated(false);

    nextStep("permissions", true);

    setRuxTokenData(null);
    return;
  };

  // If ruxTokenData is null, don't render the component
  if (!ruxTokenData) {
    return null;
  }

  // Handle both the new multiple accounts format and the old single account format
  const { accounts, carriers } = ruxTokenData;

  return (
    <>
      <StepBody>
        <StepHeading
          title="Welcome back!"
          description="Select a saved account to instantly share you insurance information."
        />
        {accounts.length > 0 ? (
          <RadioGroup
            value={selectedAccount}
            onChange={setSelectedAccount}
            className="flex flex-col gap-3"
          >
            {accounts.map((account, index) => {
              // Find the carrier that matches this account
              const carrier =
                carriers.find((c) => c.id === account.carrier) ||
                carriers[index] ||
                {};

              return (
                <Account
                  key={carrier.id || index}
                  account={account}
                  carrier={carrier}
                  onClick={() => handleAccountSelect(index)}
                />
              );
            })}
          </RadioGroup>
        ) : (
          <p className="text-body text-ink-secondary">No saved accounts found.</p>
        )}
      </StepBody>

      <StepFooter>
        {accounts.length > 0 && selectedAccount && (
          <>
            <Button
              name="continue"
              type="submit"
              onClick={handleUseExistingAccount}
            >
              Continue
            </Button>
            <Button
              variant="secondary"
              name="choose-different"
              onClick={handleChooseDifferent}
              type="button"
            >
              Choose a different carrier
            </Button>
            <Button
              variant="secondary"
              name="not-my-accounts"
              onClick={handleNotMyAccounts}
              type="button"
            >
              Continue as guest
            </Button>
          </>
        )}
      </StepFooter>
    </>
  );
};

export default WelcomeBack;
